import React from 'react';
import { SundayRow, Placement, shortDate } from './model';
import { OrganLane, PlayingLane } from './Lanes';
import AtKeyboard from './AtKeyboard';
import { SEASON_VAR, badgeFor } from './tokens';

interface Props {
  rows: SundayRow[];
  row: SundayRow;
  suggestions: { name: string; instrument: string | null }[];
  onPick: (date: string) => void;
  onChanged: () => void;
}

const SEASON_LABEL: Record<string, string> = {
  advent: 'Advent', lent: 'Lent', holyWeek: 'Holy Week', easter: 'Easter',
  christmas: 'Christmas', epiphany: 'Epiphany', feast: 'Feast · white & gold',
  pentecostDay: 'The Day of Pentecost', ordinary: 'Season after Pentecost',
};

const SundayDetail: React.FC<Props> = ({ rows, row, suggestions, onPick, onChanged }) => {
  // Repertoire and players hang off a service. From here they attach to the
  // 10:00 when there is one, otherwise to whatever service the date has.
  const targetService = row.english?.service_id ?? row.spanish?.service_id ?? null;

  return (
    <div className="sv-detail">
      <div className="sv-datenav">
        {rows.map(r => (
          <button
            key={r.date}
            className={`sv-btn sv-mono${r.date === row.date ? ' sv-on' : ''}`}
            onClick={() => onPick(r.date)}
          >
            {shortDate(r.date)}
          </button>
        ))}
      </div>

      <div className="sv-detail-head">
        <div className="sv-detail-spine" style={{ background: SEASON_VAR[row.season] }} />
        <div>
          <div className="sv-dateline">{row.dateLong}</div>
          <h2 className="sv-detail-title">{row.day}</h2>
          <div className="sv-eyebrow" style={{ marginTop: 6 }}>
            {SEASON_LABEL[row.season] || row.season}
          </div>
        </div>
        {row.readings.length > 0 && (
          <div className="sv-readings">
            {row.readings.map(r => (
              <React.Fragment key={r.k}>
                <div className="sv-rk">{r.k}</div>
                <div>{r.v}</div>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      <div className="sv-panels">
        <div>
          <div className="sv-panel">
            <div className="sv-panel-head">
              <span>Music</span>
              <span className="sv-count">{row.placements.filter(p => !p.recurring).length} chosen</span>
            </div>
            <div className="sv-place sv-place--head">
              <div />
              <div className="sv-lang-head">10:00 English</div>
              <div className="sv-lang-head">12:30 Español</div>
            </div>
            {row.placements.length === 0
              ? <div className="sv-blank">Nothing chosen yet.</div>
              : row.placements.map(p => <DetailLine key={p.key} p={p} onChanged={onChanged} />)}
          </div>

          {row.ideas.length > 0 && (
            <div className="sv-panel cl-candidate-list">
              <div className="sv-panel-head"><span>Song ideas · not yet chosen</span></div>
              {row.ideas.map((t, i) => <div key={i} className="sv-candidate">{t}</div>)}
            </div>
          )}
        </div>

        <div>
          <div className="sv-panel sv-panel--repertoire">
            <div className="sv-panel-head"><span>Organ repertoire</span></div>
            <OrganLane serviceId={targetService} works={row.organ} onChanged={onChanged} />
          </div>

          <div className="sv-panel">
            <div className="sv-panel-head"><span>Playing</span></div>
            <PlayingLane
              serviceId={targetService}
              people={row.people}
              suggestions={suggestions}
              onChanged={onChanged}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const DetailLine: React.FC<{ p: Placement; onChanged: () => void }> = ({ p, onChanged }) => {
  const badge = p.recurring ? null : badgeFor(p.enPerformanceType);
  return (
    <div className={`sv-place sv-editable${p.recurring ? ' sv-recurring' : ''}`}>
      <div className="sv-slot">{p.slot}</div>

      <div className="sv-lang">
        {p.en && <span className="sv-title-en">{p.en}</span>}
        {badge && <span className={`sv-badge ${badge.className}`}>{badge.label}</span>}
        {p.enSelectionId && (
          <AtKeyboard selectionId={p.enSelectionId} on={p.enAtOrgan} onChanged={onChanged} />
        )}
        {p.en && !p.enHasFile && <span className="sv-nofile" title="no sheet music on file">◇</span>}
        {p.enComposer && !p.recurring && <span className="sv-composer">{p.enComposer}</span>}
        {p.enNotes && <span className="sv-note">{p.enNotes}</span>}
      </div>

      <div className="sv-lang">
        {p.es && <span className="sv-title-es">{p.es}</span>}
      </div>
    </div>
  );
};

export default SundayDetail;
